import {
    createContext,
    ReactNode,
    useContext,
    useEffect,
    useState,
} from "react";
import { useParams } from "react-router-dom";
import { PERSONAL_DATA_ENDPOINT } from "../api/endpoints";
import { fetcher } from "../api/utils";
import { Test } from "../types";

interface TestContextType {
    test: Test | null | undefined;
    loading: boolean;
    error: string | null;
}

const TestContext = createContext<TestContextType | undefined>(undefined);

interface TestProviderProps {
    children: ReactNode;
}

export const TestProvider = ({ children }: TestProviderProps) => {
    const [test, setTest] = useState<Test | null | undefined>();
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const { testId } = useParams();

    useEffect(() => {
        const getTest = async () => {
            try {
                if (!testId) {
                    setError("Test ID not provided");
                    setTest(null);
                    setLoading(false);
                    return;
                }
                const test = await fetcher(PERSONAL_DATA_ENDPOINT, {
                    params: { hidden: false, test_id: testId },
                });
                const { error, navbar_status } = test;
                if (error) {
                    setError(error);
                    setTest(null);
                    setLoading(false);
                    return;
                }
                setTest({
                    id: parseInt(testId),
                    navbarStatus: navbar_status,
                } as Test);
                setLoading(false);
                setError(null);
            } catch (error) {
                setError("Error fetching test");
                setTest(null);
                setLoading(false);
            }
        };
        getTest();
    }, [testId]);

    return (
        <TestContext.Provider value={{ test, loading, error }}>
            {children}
        </TestContext.Provider>
    );
};

export const useTest = () => {
    const context = useContext(TestContext);
    if (context === undefined) {
        throw new Error("useTest must be used within an TestProvider");
    }
    return context;
};
